import { supabase } from "@/integrations/supabase/client";
import { Ad } from "@/models/ad";

// Utilidad para obtener el email y nombre del vendedor
async function getSellerInfo(sellerId: string) {
  if (!sellerId) return { fullName: "Desconocido", email: null };
  const { data, error } = await supabase
    .from("profiles")
    .select("full_name, email")
    .eq("id", sellerId)
    .maybeSingle();

  if (error || !data) return { fullName: "Desconocido", email: null };
  return {
    fullName: data.full_name || "Desconocido",
    email: data.email || null,
  };
}

export async function addAdToSupabase(ad: any) {
  try {
    console.log("=== CREACIÓN DE ANUNCIO ===");
    console.log("Datos del anuncio recibidos:", ad);

    // Validar que las pantallas sean un array válido
    if (!Array.isArray(ad.screens) || ad.screens.length === 0) {
      throw new Error("El anuncio debe tener al menos una pantalla");
    }

    // Asegurarse de que seller_id sea un UUID válido
    if (!ad.seller_id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ad.seller_id)) {
      throw new Error("ID de vendedor inválido");
    }

    // Crear objeto con solo los campos básicos necesarios y asegurar tipos correctos
    const dbAd = {
      client_name: String(ad.client_name || ''),
      start_date: ad.start_date,
      end_date: ad.end_date,
      amount: Number(ad.amount),
      screen_id: ad.screens[0].screenId, // Ya debe ser UUID
      screens_json: ad.screens,
      seller_id: ad.seller_id, // Ya validado como UUID
      seller_name: String(ad.seller_name || ''),
      seller_email: String(ad.seller_email || ''),
      material_url: String(ad.material_url || ''),
      type: String(ad.type || 'image'),
      ad_type: String(ad.ad_type || 'sale'),
      is_hidden: Boolean(ad.is_hidden || false),
      is_expired: Boolean(ad.is_expired || false),
      version: Number(ad.version || 1)
    };

    console.log("Intentando crear anuncio con datos formateados:", dbAd);

    // Crear el anuncio con los campos necesarios
    const { data, error } = await supabase
      .from("ads")
      .insert([dbAd])
      .select()
      .single();

    if (error) {
      console.error("Error al insertar anuncio:", error);
      throw error;
    }

    console.log("✅ Anuncio creado exitosamente:", data);
    return data;
  } catch (error) {
    console.error("Error en addAdToSupabase:", error);
    throw error;
  }
}

export async function updateAdInSupabase(ad: Ad) {
  try {
    console.log("=== ACTUALIZACIÓN DE ANUNCIO ===");
    console.log("ID del anuncio:", ad.id);
    console.log("Datos a actualizar:", ad);

    // Construir SOLO los campos básicos, evitando cualquier referencia a usuarios
    const updateData = {
      client_name: ad.clientName,
      start_date: ad.startDate,
      end_date: ad.endDate,
      amount: ad.amount,
      screens_json: ad.screens,
      screen_id: ad.screens[0]?.screenId,
      type: ad.type,
      ad_type: ad.adType,
      material_url: ad.materialUrl || "",
      is_hidden: ad.isHidden,
      is_expired: ad.isExpired,
      duration: ad.duration || 0,
      version: ad.version,
    };

    console.log("Datos formateados para Supabase (solo campos básicos):", updateData);

    // Actualizar SOLO los campos básicos del anuncio
    const { data, error } = await supabase
      .from("ads")
      .update(updateData)
      .eq("id", ad.id)
      .select()
      .single();

    if (error) {
      console.error("❌ Error al actualizar anuncio:", error);
      throw error;
    }

    console.log("✅ Anuncio actualizado exitosamente:", data);
    return data;
  } catch (error) {
    console.error("❌ Error en updateAdInSupabase:", error);
    throw error;
  }
}

export async function deleteAdFromSupabase(adId: string) {
  console.log("=== ELIMINACIÓN DE ANUNCIO ===");
  console.log("ID del anuncio a eliminar:", adId);
  
  try {
    // 1. Obtener el anuncio para saber qué pantallas liberar
    const { data: ad, error: getError } = await supabase
      .from("ads")
      .select("*")
      .eq("id", adId)
      .single();

    if (getError) {
      console.error("Error al obtener anuncio para eliminar:", getError);
      throw new Error(`Error al obtener anuncio: ${getError.message}`);
    }

    if (!ad) {
      console.error("No se encontró el anuncio con ID:", adId);
      throw new Error("No se encontró el anuncio");
    }

    console.log("Anuncio encontrado:", ad);

    // 2. Eliminar la referencia en la tabla facturas
    const { error: facturaError } = await supabase
      .from("facturas")
      .update({ ad_id: null })
      .eq("ad_id", adId);

    if (facturaError) {
      console.error("Error al actualizar facturas:", facturaError);
      throw new Error(`Error al actualizar facturas: ${facturaError.message}`);
    }

    console.log("Referencias en facturas actualizadas");

    // 3. Eliminar definitivamente el anuncio
    const { error: deleteError } = await supabase
      .from("ads")
      .delete()
      .eq("id", adId);

    if (deleteError) {
      console.error("Error al eliminar anuncio:", deleteError);
      throw new Error(`Error al eliminar anuncio: ${deleteError.message}`);
    }

    // 4. Verificar que el anuncio se haya eliminado
    const { data: checkAd } = await supabase
      .from("ads")
      .select("id")
      .eq("id", adId)
      .single();

    if (checkAd) {
      throw new Error("El anuncio no se eliminó correctamente");
    }
    
    console.log("✅ Anuncio eliminado exitosamente");
    return true;
  } catch (error) {
    console.error("Error en deleteAdFromSupabase:", error);
    throw error;
  }
}

export async function updateAdMaterialUrl(adId: string, materialUrl: string) {
  try {
    console.log("=== ACTUALIZACIÓN SIMPLE DE MATERIAL_URL ===");
    console.log("ID del anuncio:", adId);
    console.log("Nueva URL del material:", materialUrl);

    // UPDATE directo y simple - las políticas RLS manejan los permisos
    const { data, error } = await supabase
      .from('ads')
      .update({ material_url: materialUrl })
      .eq('id', adId)
      .select('id, client_name, material_url');

    if (error) {
      console.error("❌ Error al actualizar:", error);
      throw new Error(`No se pudo actualizar: ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error("No se encontró el anuncio o no tienes permisos para editarlo");
    }

    console.log("✅ Material URL actualizado exitosamente:", data[0]);
    return true;
  } catch (error) {
    console.error("❌ Error en updateAdMaterialUrl:", error);
    throw error;
  }
}
